import React, { FC, useState, useEffect } from "react";
import { observer } from "mobx-react";
import styled from "styled-components";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import { PipelineCreateFields, PipelineModel, PipelineType } from "@web/models";
import { useStores } from "@web/stores/context";
import { EditPipeline } from "@web/components/Flow/Pipeline/EditPipeline";
import { PipelineList } from "@web/components/Flow/Pipeline/PipelineList";
import { CreateButton } from "@web/components/Flow/CreateButton";
import { ErrorBanner } from "@web/components/Error/ErrorBanner";
import { useConfig } from "@config/context";

export const NotificationsContainer: FC = observer(() => {
  const intl = useIntl();

  const {
    flowStore,
    tagStore,
    sectionStore,
    classificationStore,
    recordStore,
  } = useStores();
  const { canManageFlows } = useConfig();
  const [addPipeline, setAddPipeline] = useState<PipelineType>();
  const [selectedPipeline, selectPipeline] = useState<PipelineModel>();

  useEffect(() => {
    flowStore.loadNotifications();
    flowStore.loadReminders();
    flowStore.loadEvents();
  }, []);

  const handleCancel = () => {
    setAddPipeline(undefined);
    selectPipeline(undefined);
  };

  const handleAddPipeline = async (fields: PipelineCreateFields) => {
    const pipeline = await flowStore.addPipeline(fields);
    setAddPipeline(undefined);

    if (pipeline) {
      selectPipeline(pipeline);
    }
  };

  if (!canManageFlows) {
    return <ErrorBanner type="noAccess" />;
  }

  if (addPipeline || selectedPipeline) {
    const type =
      addPipeline || selectedPipeline?.pipelineType || "notification";
    return (
      <EditPipeline
        type={type}
        pipeline={selectedPipeline}
        entryStatuses={recordStore.entryStatuses}
        isCreatingPipeline={flowStore.isCreatingPipeline}
        addPipeline={handleAddPipeline}
        onCancel={handleCancel}
        findTags={tagStore.findTags}
        findSections={sectionStore.findSections}
        findClassifications={classificationStore.findClassifications}
      />
    );
  }

  return (
    <_wrap>
      <_header>
        <h1>
          <FormattedMessage {...texts.notificationTitle} />
        </h1>
        <CreateButton
          text={intl.formatMessage(texts.addNotification)}
          onClick={() => setAddPipeline("notification")}
        />
      </_header>

      <PipelineList
        pipelines={flowStore.pipelines.notification}
        onPipelineClick={selectPipeline}
      />
      <_header>
        <h1>
          <FormattedMessage {...texts.reminderTitle} />
        </h1>
        <CreateButton
          text={intl.formatMessage(texts.addReminder)}
          onClick={() => setAddPipeline("reminder")}
        />
      </_header>

      <PipelineList
        pipelines={flowStore.pipelines.reminder}
        onPipelineClick={selectPipeline}
      />
    </_wrap>
  );
});

const _wrap = styled.div`
  width: 100%;
  font-size: 13px;
  padding: 0px 25px;
  margin-top: 0px;
`;

const _header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  button {
    margin-left: 25px;
    white-space: nowrap;
  }
`;

const texts = defineMessages({
  notificationTitle: {
    id: "settings.notifications.title",
    defaultMessage: "Notifications",
  },
  addNotification: {
    id: "settings.notifications.add",
    defaultMessage: "Add new notification",
  },
  reminderTitle: {
    id: "settings.notifications.reminder.title",
    defaultMessage: "Reminder destinations",
  },
  addReminder: {
    id: "settings.notifications.reminder.add",
    defaultMessage: "Add new reminder",
  },
});
